import { useMutation, useQueryClient } from "@tanstack/react-query"
import { addPaymentMethod } from "api"
import { useAuth } from "context/AuthContext"
import { useToast } from "hooks/index.js"
import { useState } from "react"
import styled from "styled-components"
import { ButtonPrimary } from "ui/base/Button/ButtonStyled"
import { HelperText } from "ui/base/helperText/HelperText.jsx"
import { InputSignUp } from "ui/base/InputSignUp/InputSignUp"
import { Modal } from "ui/base/modal/Modal"
import { hideScrollBar } from "ui/base/scrollbarStyle/scrollbarStyle"
import { ProfileIcon } from "ui/icons"
import { PlusIcon } from "ui/icons/PlusIcon"
import { themeColor } from "ui/theme/index.js"
import { filterOnlyEnglishCharacter, numberOnly, removeEmojis, specialCharacterRemove } from "utils"
import CryptoJS from "crypto-js";

export const AddMethodModal = () => {
    const queryClient = useQueryClient()
    const [open, setOpen] = useState(false)
    const { userData } = useAuth();

    const payoutInfo = userData?.teacher?.payment_info || [];

    const handleClose = () => setOpen(false)

    const numberList =
        payoutInfo.map((card) => {
            const bytes1 = CryptoJS.AES.decrypt(card.routing_number, userData.email)
            const bytes2 = CryptoJS.AES.decrypt(card.account_number, userData.email)
            let routing_number = bytes1.toString(CryptoJS.enc.Utf8)
            let account_number = bytes2.toString(CryptoJS.enc.Utf8)

            return {
                routing_number,
                account_number,
            }
        }) || []

    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        routing_number: "",
        account_number: "",
        confirm_account_number: "",
    })

    const [formError, setFormError] = useState({
        first_name: "",
        last_name: "",
        routing_number: "",
        account_number: "",
        confirm_account_number: "",
    })

    const { mutate, isLoading } = useMutation({
        mutationFn: addPaymentMethod,
        onError: (err) => {
            useToast(
                "error",
                err?.response?.data?.message || "Error: Add payout method"
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userDetail"] })
            setFormData({})
            setFormError({})

            handleClose();
        },
    })

    const onFormChange = (key, value) => {
        const isError = value?.trim() === "" || value === undefined

        if (key === "first_name" || key === "last_name") {
            setFormData((prevState) => ({
                ...prevState,
                [key]: filterOnlyEnglishCharacter(removeEmojis(value)),
            }))
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [key]: specialCharacterRemove(removeEmojis(value)),
            }))
        }

        if (key === "account_number" || key === "confirm_account_number") {
            setFormError((prevState) => ({
                ...prevState,
                account_number: "",
                confirm_account_number: "",
            }))
        }

        setFormError((prevState) => ({
            ...prevState,
            [key]: isError ? "Must be filled" : "",
        }))
    }


    const onSubmit = () => {

        let isError = false
        const allEntires = Object.entries(formData)
        allEntires.map((item) => {
            if (item[1].trim().length === 0) {
                isError = true
                setFormError((prevState) => ({
                    ...prevState,
                    [item[0]]: "Must be filled",
                }))
            }
        })

        if (
            Array.isArray(numberList) &&
            numberList.find((it) => it?.account_number === formData.account_number)
        ) {
            isError = true
            useToast("error", "This account number has already been added")
            return
        }
        if (formData.account_number !== formData.confirm_account_number) {
            isError = true
            useToast("error", "Account numbers must match")
            setFormError((prevState) => ({
                ...prevState,
                account_number: "Must be same",
                confirm_account_number: "Must be same",
            }))
            return
        }

        if (isError) {
            return
        }

        mutate({ ...formData, email: userData?.email })
    }

    return (
        <>
            <Container>
                <IconContainer
                    style={{ cursor: "pointer" }}
                    onClick={() => setOpen(true)}>
                    <PlusIcon size={22} color='#554DF1' />
                </IconContainer>

                <HelperText
                    color='blue'
                    onClick={() => setOpen(true)}
                    weight='600'
                    style={{ cursor: "pointer" }}>
                    Add payout method
                </HelperText>
            </Container>
            <Modal
                show={open}
                handleClose={handleClose}
                minHeight={200}
                maxWidth={550}>
                <ModalContainer>
                    <UserInfoOuterContainer>
                        <UserInfoInnerContainer>
                            <HelperText style={{ gridColumn: "span 2" }}>
                                <HelperText> Add your bank account information</HelperText>
                                <br />
                                <HelperText>
                                    We process direct deposits for your payouts after the class.
                                    Direct deposits are managed and secured by Stripe.
                                </HelperText>
                            </HelperText>
                            <InputSignUp
                                id='firstName'
                                placeholder='First name'
                                icon={<ProfileIcon />}
                                value={formData.first_name}
                                setValue={(input) => onFormChange("first_name", input)}
                                isError={formError.first_name}
                                errorText={formError.first_name}
                                maxLength={20}
                            />
                            <InputSignUp
                                id='lastName'
                                placeholder='Last name'
                                icon={<ProfileIcon />}
                                value={formData.last_name}
                                setValue={(input) => onFormChange("last_name", input)}
                                isError={formError.last_name}
                                errorText={formError.last_name}
                                maxLength={20}
                            />
                        </UserInfoInnerContainer>

                        <HelperText color='placeholder'>
                            Enter the account holder name exactly as it appears on bank statements
                        </HelperText>
                    </UserInfoOuterContainer>
                    <AccountNumberContainer>
                        <InputSignUp
                            onInput={numberOnly}
                            placeholder='Routing number'
                            value={formData.routing_number}
                            setValue={(input) => onFormChange("routing_number", input)}
                            isError={formError.routing_number}
                            errorText={formError.routing_number}
                            maxLength={20}
                        />
                        <InputSignUp
                            onInput={numberOnly}
                            placeholder='Account number'
                            value={formData.account_number}
                            setValue={(input) => onFormChange("account_number", input)}
                            isError={formError.account_number}
                            errorText={formError.account_number}
                            maxLength={20}
                        />
                        <InputSignUp
                            onInput={numberOnly}
                            placeholder='Confirm account number'
                            value={formData.confirm_account_number}
                            setValue={(input) => onFormChange("confirm_account_number", input)}
                            isError={formError.confirm_account_number}
                            errorText={formError.confirm_account_number}
                            maxLength={20}
                        />
                    </AccountNumberContainer>

                    <ButtonPrimary
                        onClick={onSubmit}
                        disabled={isLoading}
                    >
                        Submit
                    </ButtonPrimary>
                </ModalContainer>
            </Modal>

        </>
    )
}

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;

  margin-top: 1rem;
  margin-bottom: 1rem;
`

const IconContainer = styled.div`
  height: ${({ size }) => (size ? size + "px" : "45px")};
  width: ${({ size }) => (size ? size + "px" : "45px")};
  background-color: ${themeColor("lightBlue")};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 99px;
`

export const ModalContainer = styled.div`
  width: 100%;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
  font-family: var(--font-secondary);
  ${hideScrollBar}
`

const UserInfoOuterContainer = styled.div`
  display: grid;
  gap: 0.3rem;
`
const UserInfoInnerContainer = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr;
  align-items: start;
`

const AccountNumberContainer = styled.div`
  display: grid;
  gap: 1rem;
` 